import {isEmpty} from 'lodash'
import React, {useCallback, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {blockPopUp} from '../../../../_gori/helpers/BrowserHelpers'
import {DatePicker} from '../../../../_gori/helpers/components/DatePicker'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {
  Button,
  ConfirmActionSwal,
  InputSearch,
  TableHeader,
} from '../../../../_gori/partials/widgets'
import {ActionAllModal} from '../../common'
import {SET_UP_MODAL_ALL} from '../../common/core/_const'
import {NoticePrintPackingSlips} from '../../orders'
import {isCreatedWithinLast30Days} from '../../orders/core/_helpers'
import ShipmentService from '../core/_requests'
import UseRoutesPermission from '../../../../_gori/hooks/UseRoutesPermission'
import {ShipmentRefund} from '../core/_const'
import {DEFAULT_DATE_FILTER} from '../../../../_gori/constants'

type Props = {
  selectedListId?: any
  reloadTable: any
  dataSettings: any
}

const ShipmentFilter: React.FC<Props> = ({selectedListId, reloadTable, dataSettings}) => {
  const intl = useIntl()
  const {routes} = UseRoutesPermission()
  const {newCancelToken, isCancel} = useCancelToken()
  const [loading, setLoading] = useState<any>({
    print: false,
    printPackingSlip: false,
  })
  const [showModal, setShowModal] = useState<any>({
    confirmActionSwal: false,
    requestRefund: false,
    printPackingSlip: false,
  })
  const [dataModal, setDataModal] = useState<any>({
    requestRefund: undefined,
    printPackingSlip: undefined,
    noticePrintPackingSlip: {},
  })
  const [showSwal, setShowSwal] = useState<any>({
    requestRefund: {callback: undefined, status: false},
    noticePrintPackingSlip: false,
  })

  const shipmentIds = useMemo(() => {
    return {
      print_label: selectedListId,
      print_packing_slip: selectedListId,
      request_refund: selectedListId.filter((row) => isEmpty(row.original?.refund)) || [],
    }
  }, [selectedListId])

  const handlePrintBatchLabel = useCallback(async () => {
    let data: any = {
      shipments: [],
      refunds: [],
    }
    shipmentIds.print_label.forEach((row: any) => {
      if (!isCreatedWithinLast30Days(row.original.created_at)) return

      data.shipments.push({
        gori_shipment_id: row.original.id,
        label: row.original.label.image_url,
      })
      if (
        row.original.refund?.status === ShipmentRefund.SUBMITTED ||
        row.original.refund?.status === ShipmentRefund.REFUNDED
      ) {
        data.refunds.push(row.original.id)
      }
    })
    if (data.shipments.length === 0) {
      toast.warning(
        intl.formatMessage({
          id: 'PRINT_LABELS_WITHIN_LAST_30_DAYS_REQUIRED',
        })
      )
      return
    }
    if (data.shipments.length === data.refunds.length) {
      toast.warning(
        intl.formatMessage({
          id: 'PRINT_LABELS_REFUNDED',
        })
      )
      return
    }
    if (data.shipments.length > 0) {
      setLoading((prev) => ({...prev, print: true}))
      const config = {cancelToken: newCancelToken()}
      const payload = {
        shipments: data.shipments,
      }
      await ShipmentService.print(payload, config)
        .then((response) => {
          if (blockPopUp(response.label_url)) {
            toast.warning(
              intl.formatMessage({
                id: 'POP_UP_IS_BLOCKED',
              })
            )
          }
        })
        .catch((error) => {
          if (isCancel(error)) return
        })
        .finally(() => {
          setLoading((prev) => ({...prev, print: false}))
        })
    }
  }, [intl, isCancel, newCancelToken, shipmentIds.print_label])

  const handleRequestRefundAll = useCallback(() => {
    if (isEmpty(shipmentIds.request_refund)) return

    setDataModal((prev) => ({
      ...prev,
      requestRefund: shipmentIds.request_refund,
    }))
    setShowModal((prev) => ({...prev, requestRefund: true}))
  }, [shipmentIds.request_refund])

  const handleCloseModal = () => {
    setShowModal((prev) => ({...prev, requestRefund: false}))
    setDataModal((prev) => ({...prev, requestRefund: undefined}))
    reloadTable()
  }

  const handlePrintPackingSlips = () => {
    const {
      packing_slip: {disable},
    } = dataSettings
    if (disable) {
      const configNoticePackingSlip = {
        title: 'PRINT_PACKING_SLIPS',
        content: 'DISABLE_PACKING_SLIPS_MESSAGE',
      }
      setDataModal((prev) => ({
        ...prev,
        noticePrintPackingSlip: configNoticePackingSlip,
      }))
      setShowModal((prev) => ({...prev, noticePrintPackingSlip: true}))
      return
    }

    setDataModal((prev) => ({
      ...prev,
      printPackingSlip: shipmentIds.print_packing_slip,
    }))
    setShowModal((prev) => ({...prev, printPackingSlip: true}))
  }

  return (
    <>
      {showModal.requestRefund && (
        <ActionAllModal
          show={showModal.requestRefund}
          data={dataModal?.requestRefund}
          setUpModal={SET_UP_MODAL_ALL.REFUND_REQUEST}
          handleClose={handleCloseModal}
        />
      )}
      {showModal.printPackingSlip && (
        <ActionAllModal
          show={showModal.printPackingSlip}
          data={dataModal?.printPackingSlip}
          setUpModal={SET_UP_MODAL_ALL.PACKING_SLIPS}
          handleClose={() => {
            setShowModal((prev) => ({...prev, printPackingSlip: false}))
          }}
          isShipments={true}
        />
      )}
      {showSwal &&
        Object.entries(showSwal).map(([key, value]: [any, any], index) => {
          return (
            <ConfirmActionSwal
              key={index}
              show={value.status}
              title={intl.formatMessage({id: 'REQUEST_REFUND'})}
              message={intl.formatMessage({id: 'ARE_YOU_SURE'})}
              messageCancel={intl.formatMessage({id: 'NO'})}
              handleCallBack={value.callback}
              handleClose={() =>
                setShowSwal((prev) => ({...prev, [key]: {...prev[key], status: false}}))
              }
            />
          )
        })}
      {showModal.noticePrintPackingSlip && (
        <NoticePrintPackingSlips
          show={showModal.noticePrintPackingSlip}
          handleClose={() => setShowModal((prev) => ({...prev, noticePrintPackingSlip: false}))}
          configModal={dataModal.noticePrintPackingSlip}
        />
      )}
      <TableHeader className='min-h-1px my-5 mb-6 d-flex flex-column'>
        <div className='d-flex flex-wrap gap-2 justify-content-between align-items-start filter-toolbar-44'>
          <div className='col-12 col-lg-6 d-flex align-items-start position-relative input-search-wrapper'>
            <InputSearch
              placeholder={intl.formatMessage({id: 'TRACKING_NUMBER_SEARCH_PLACEHOLDER'})}
            />
          </div>
          <DatePicker
            justifyContent='start'
            className='col-12 col-md-auto bg-input-dark'
            name={intl.formatMessage({id: 'SHIP_DATE'})}
            dateStart={DEFAULT_DATE_FILTER.SHIPMENTS}
          />
        </div>
        <div className='card-toolbar flex-row-reverse'>
          <div
            className={`d-flex flex-wrap gap-2 justify-content-end align-items-center ${
              !(selectedListId.length > 0) && 'd-none'
            }`}
          >
            <div className='fw-bolder me-5'>
              <span className='me-2'>{selectedListId.length}</span>
              {intl.formatMessage({id: 'SELECTED'})}
            </div>
            {!isEmpty(shipmentIds.print_label) && (
              <Button
                className='btn btn-light-blue border border-blue me-3'
                label={intl.formatMessage({id: 'PRINT_LABELS'})}
                loadingText={intl.formatMessage({id: 'PRINT_LABELS'})}
                loading={loading.print}
                event={() => handlePrintBatchLabel()}
              />
            )}
            {!isEmpty(shipmentIds.print_packing_slip) &&
              routes.SHIPMENTS_PACKING_SLIP.hasPermission && (
                <Button
                  className='btn btn-light-blue border border-blue me-3'
                  label={intl.formatMessage({id: 'PRINT_PACKING_SLIPS'})}
                  loadingText={intl.formatMessage({id: 'PRINT_PACKING_SLIPS'})}
                  event={handlePrintPackingSlips}
                />
              )}
            {!isEmpty(shipmentIds.request_refund) && routes.SHIPMENTS_REFUND.hasPermission && (
              <Button
                event={() => {
                  setShowSwal((prev) => ({
                    ...prev,
                    requestRefund: {
                      status: true,
                      callback: () => handleRequestRefundAll(),
                    },
                  }))
                }}
                className='btn btn-light-danger fw-bolder me-3'
                label={intl.formatMessage({id: 'REQUEST_REFUND'})}
              />
            )}
          </div>
        </div>
      </TableHeader>
    </>
  )
}

export {ShipmentFilter}
